import { AlertCircle, FileText, Loader2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { statusMeta } from '@/lib/documentServices';

const tones = {
  gold: 'border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#7A5A05]',
  neutral: 'border-[#062B1A]/15 bg-[#062B1A]/5 text-[#062B1A]/70',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

export function ServiceStatusBadge({ kind, status, className }) {
  const meta = statusMeta(kind, status);
  return (
    <span className={cn('inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold', tones[meta.tone] || tones.neutral, className)}>
      {meta.label}
    </span>
  );
}

export function ServicePageHeader({ eyebrow, title, description, actions, backTo, backLabel = 'Back' }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        {backTo && (
          <Link to={backTo} className="mb-2 inline-flex text-sm font-medium text-[#062B1A]/60 hover:text-[#062B1A]">
            ← {backLabel}
          </Link>
        )}
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#D4AF37]">{eyebrow}</p>}
        <h1 className="mt-1 text-2xl font-semibold text-[#062B1A] sm:text-3xl" style={{ fontFamily: 'var(--font-display)' }}>
          {title}
        </h1>
        {description && <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#062B1A]/60">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ServiceLoading({ label = 'Loading document services…' }) {
  return (
    <div className="flex min-h-[260px] items-center justify-center rounded-xl border border-[rgba(6,43,26,0.08)] bg-white">
      <div className="text-center text-sm text-[#062B1A]/55">
        <Loader2 className="mx-auto mb-3 size-6 animate-spin text-[#D4AF37]" />
        {label}
      </div>
    </div>
  );
}

export function ServiceError({ message, onRetry }) {
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 size-5 shrink-0" />
        <div>
          <p className="font-semibold">Document Services could not be loaded</p>
          <p className="mt-1 text-sm">{message}</p>
          {onRetry && <button type="button" onClick={onRetry} className="mt-3 text-sm font-semibold underline">Try again</button>}
        </div>
      </div>
    </div>
  );
}

export function ServiceDisabledNotice({ admin = false }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900" role="status">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 size-5 shrink-0" />
        <div>
          <p className="font-semibold">Ordering is currently in setup mode</p>
          <p className="mt-1 text-sm leading-relaxed">
            {admin
              ? 'Products and workflows can be configured, but student and public checkout remain disabled.'
              : 'You can review available services, but checkout is not open yet. No payment can be started.'}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ServiceEmpty({ title = 'Nothing here yet', description, action }) {
  return (
    <div className="rounded-xl border border-dashed border-[#062B1A]/15 bg-white px-6 py-14 text-center">
      <FileText className="mx-auto size-10 text-[#062B1A]/20" />
      <h2 className="mt-3 text-base font-semibold text-[#062B1A]">{title}</h2>
      {description && <p className="mx-auto mt-1 max-w-lg text-sm text-[#062B1A]/50">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function TaxExemptLine({ amount = 'C$0.00' }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-[#062B1A]/60">Tax <span className="ml-1 rounded bg-[#062B1A]/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase">Exempt</span></span>
      <span className="font-medium text-[#062B1A]">{amount}</span>
    </div>
  );
}
